import React, { useEffect, useRef, useState, useCallback } from 'react';
import { prepareWithSegments, layoutWithLines } from '@chenglou/pretext';
import ExperimentNav from '../../components/ExperimentNav';
import { experiments } from '../../App';
import './TextRepel.css';

const PROMPT = '기기의 물리적인 회전에 따라 텍스트가 해당 방향으로 쏟아지는 페이지. 중심이 잘 맞을수록 텍스트는 원래대로 돌아옴. 벽에 부딪힐 때 작은 텍스트가 파티클처럼 터지는 연출.';

const DISPLAY_TEXT = 'GRAVITY\nTYPE';
const SUB_TEXT = 'tilt your device to pour the letters';

const GRAVITY_STRENGTH = 0.6;
const RETURN_SPEED = 0.04;
const FRICTION = 0.97;
const BOUNCE = 0.5;
const WALL_PADDING = 10;
const BURST_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%&*<>[]{}~'.split('');
const MAX_BURSTS = 200;
const LEVEL_THRESHOLD = 5; // degrees — considered "level"

interface CharParticle {
  char: string;
  originX: number;
  originY: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  fontSize: number;
  width: number;
  height: number;
}

interface BurstParticle {
  char: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  fontSize: number;
  opacity: number;
  life: number;
  maxLife: number;
  rotation: number;
  rotSpeed: number;
}

const TextRepel: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<CharParticle[]>([]);
  const burstsRef = useRef<BurstParticle[]>([]);
  const tiltRef = useRef({ x: 0, y: 0 }); // gravity direction
  const animRef = useRef<number>(0);
  const hasOrientationRef = useRef(false);
  const mouseRef = useRef({ x: 0, y: 0, active: false });
  const hintRef = useRef<HTMLDivElement>(null);
  const [tiltDisplay, setTiltDisplay] = useState({ x: 0, y: 0 });
  const [charCount, setCharCount] = useState(0);
  const [burstCount, setBurstCount] = useState(0);
  const [permissionState, setPermissionState] = useState<'pending' | 'prompt' | 'granted' | 'desktop'>('pending');
  const exp = experiments.find(e => e.id === 'text-repel');

  const spawnBurst = useCallback((x: number, y: number, vx: number, vy: number) => {
    const bursts = burstsRef.current;
    const count = 3 + Math.floor(Math.random() * 4);
    const speed = Math.sqrt(vx * vx + vy * vy);
    const baseSize = Math.max(6, Math.min(10, speed * 0.8));

    for (let i = 0; i < count; i++) {
      if (bursts.length >= MAX_BURSTS) {
        // Recycle oldest
        bursts.shift();
      }
      const angle = Math.random() * Math.PI * 2;
      const force = (1 + Math.random() * 3) * Math.min(speed * 0.3, 4);
      bursts.push({
        char: BURST_CHARS[Math.floor(Math.random() * BURST_CHARS.length)],
        x,
        y,
        vx: Math.cos(angle) * force + vx * 0.2,
        vy: Math.sin(angle) * force + vy * 0.2,
        fontSize: baseSize + Math.random() * 4,
        opacity: 0.8 + Math.random() * 0.2,
        life: 0,
        maxLife: 30 + Math.random() * 30,
        rotation: Math.random() * Math.PI * 2,
        rotSpeed: (Math.random() - 0.5) * 0.3,
      });
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const initParticles = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const particles: CharParticle[] = [];

      const mainFontSize = Math.min(w * 0.1, 80);
      const mainFont = `bold ${mainFontSize}px "Press Start 2P", monospace`;

      const lines = DISPLAY_TEXT.split('\n');

      try {
        let totalHeight = 0;
        const lineData: { text: string; width: number }[] = [];

        for (const line of lines) {
          const prepared = prepareWithSegments(line, mainFont);
          const result = layoutWithLines(prepared, w * 0.95, mainFontSize * 1.4);
          const lw = result.lines[0]?.width || 0;
          lineData.push({ text: line, width: lw });
          totalHeight += mainFontSize * 1.3;
        }

        const startY = (h - totalHeight) / 2;

        lineData.forEach((ld, lineIdx) => {
          const lineY = startY + lineIdx * mainFontSize * 1.3 + mainFontSize;
          const lineStartX = (w - ld.width) / 2;

          let charX = lineStartX;
          for (let i = 0; i < ld.text.length; i++) {
            const ch = ld.text[i];
            if (ch === ' ') {
              const sp = prepareWithSegments(' ', mainFont);
              const sl = layoutWithLines(sp, 9999, mainFontSize);
              charX += sl.lines[0]?.width || mainFontSize * 0.3;
              continue;
            }

            const cp = prepareWithSegments(ch, mainFont);
            const cl = layoutWithLines(cp, 9999, mainFontSize);
            const cw = cl.lines[0]?.width || mainFontSize * 0.6;

            particles.push({
              char: ch,
              originX: charX + cw / 2,
              originY: lineY,
              x: charX + cw / 2,
              y: lineY,
              vx: 0,
              vy: 0,
              fontSize: mainFontSize,
              width: cw,
              height: mainFontSize,
            });
            charX += cw;
          }
        });
      } catch {
        const chars = DISPLAY_TEXT.replace('\n', ' ').split('');
        const approxCharW = mainFontSize * 0.7;
        const totalW = chars.length * approxCharW;
        const startX = (w - totalW) / 2;
        const centerY = h / 2;

        chars.forEach((ch, i) => {
          if (ch === ' ') return;
          particles.push({
            char: ch,
            originX: startX + i * approxCharW + approxCharW / 2,
            originY: centerY,
            x: startX + i * approxCharW + approxCharW / 2,
            y: centerY,
            vx: 0,
            vy: 0,
            fontSize: mainFontSize,
            width: approxCharW,
            height: mainFontSize,
          });
        });
      }

      // Sub text
      const subFontSize = Math.min(w * 0.016, 13);
      const subFont = `${subFontSize}px "Press Start 2P", monospace`;

      try {
        const subPrepared = prepareWithSegments(SUB_TEXT, subFont);
        const subResult = layoutWithLines(subPrepared, w * 0.9, subFontSize * 1.5);
        const mainBottom = (h + 0) / 2 + mainFontSize * 0.6;

        subResult.lines.forEach((line, lineIdx) => {
          const lineY = mainBottom + 40 + lineIdx * subFontSize * 1.5 + subFontSize;
          const lineStartX = (w - line.width) / 2;

          let charX = lineStartX;
          for (let i = 0; i < line.text.length; i++) {
            const ch = line.text[i];
            if (ch === ' ') {
              const sp = prepareWithSegments(' ', subFont);
              const sl = layoutWithLines(sp, 9999, subFontSize);
              charX += sl.lines[0]?.width || subFontSize * 0.3;
              continue;
            }

            const cp = prepareWithSegments(ch, subFont);
            const cl = layoutWithLines(cp, 9999, subFontSize);
            const cw = cl.lines[0]?.width || subFontSize * 0.6;

            particles.push({
              char: ch,
              originX: charX + cw / 2,
              originY: lineY,
              x: charX + cw / 2,
              y: lineY,
              vx: 0,
              vy: 0,
              fontSize: subFontSize,
              width: cw,
              height: subFontSize,
            });
            charX += cw;
          }
        });
      } catch {
        // Skip sub text on error
      }

      particlesRef.current = particles;
      burstsRef.current = [];
      setCharCount(particles.length);
    };

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      initParticles();
    };

    // Device orientation
    const onOrientation = (e: DeviceOrientationEvent) => {
      if (!hasOrientationRef.current) {
        hasOrientationRef.current = true;
        if (hintRef.current) hintRef.current.style.opacity = '0';
      }

      const gamma = e.gamma || 0; // left-right: -90 to 90
      const beta = e.beta || 0;   // front-back: -180 to 180

      // Normalize to -1..1 range
      const nx = Math.max(-1, Math.min(1, gamma / 45));
      const ny = Math.max(-1, Math.min(1, (beta - 20) / 45)); // offset 20 for natural hold angle

      tiltRef.current.x = nx;
      tiltRef.current.y = ny;
    };

    // Mouse fallback for desktop
    const onMouseMove = (e: MouseEvent) => {
      mouseRef.current.x = e.clientX;
      mouseRef.current.y = e.clientY;
      mouseRef.current.active = true;

      if (hintRef.current) hintRef.current.style.opacity = '0';

      // Convert mouse position to tilt: center = level, edges = tilted
      const w = window.innerWidth;
      const h = window.innerHeight;
      const nx = ((e.clientX / w) - 0.5) * 2; // -1 to 1
      const ny = ((e.clientY / h) - 0.5) * 2;
      tiltRef.current.x = nx;
      tiltRef.current.y = ny;
    };

    const onMouseLeave = () => {
      mouseRef.current.active = false;
      tiltRef.current.x = 0;
      tiltRef.current.y = 0;
    };

    // Touch fallback
    const onTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;
      if (hintRef.current) hintRef.current.style.opacity = '0';

      if (!hasOrientationRef.current) {
        const w = window.innerWidth;
        const h = window.innerHeight;
        const nx = ((touch.clientX / w) - 0.5) * 2;
        const ny = ((touch.clientY / h) - 0.5) * 2;
        tiltRef.current.x = nx;
        tiltRef.current.y = ny;
      }
    };

    const onTouchEnd = () => {
      if (!hasOrientationRef.current) {
        tiltRef.current.x = 0;
        tiltRef.current.y = 0;
      }
    };

    let frameCount = 0;

    const animate = () => {
      animRef.current = requestAnimationFrame(animate);
      frameCount++;

      const w = window.innerWidth;
      const h = window.innerHeight;

      ctx.clearRect(0, 0, w, h);

      const tx = tiltRef.current.x;
      const ty = tiltRef.current.y;
      const tiltMag = Math.sqrt(tx * tx + ty * ty);
      const isLevel = tiltMag < (LEVEL_THRESHOLD / 45);

      // Update HUD every 6 frames
      if (frameCount % 6 === 0) {
        setTiltDisplay({
          x: Math.round(tx * 45),
          y: Math.round(ty * 45),
        });
        setBurstCount(burstsRef.current.length);
      }

      const particles = particlesRef.current;
      const bursts = burstsRef.current;

      // Physics for main characters
      for (const p of particles) {
        // Gravity from tilt
        const gravX = tx * GRAVITY_STRENGTH * (p.fontSize / 40);
        const gravY = ty * GRAVITY_STRENGTH * (p.fontSize / 40);

        p.vx += gravX;
        p.vy += gravY;

        // Return to origin force — stronger when level
        const returnMult = isLevel ? RETURN_SPEED * 3 : RETURN_SPEED * (1 - tiltMag * 0.7);
        const dx = p.originX - p.x;
        const dy = p.originY - p.y;
        p.vx += dx * returnMult;
        p.vy += dy * returnMult;

        // Friction
        p.vx *= FRICTION;
        p.vy *= FRICTION;

        // Apply velocity
        p.x += p.vx;
        p.y += p.vy;

        // Wall collision
        const halfW = p.width / 2;
        const halfH = p.height / 2;
        const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);

        // Left wall
        if (p.x - halfW < WALL_PADDING) {
          p.x = WALL_PADDING + halfW;
          if (speed > 1.5) spawnBurst(WALL_PADDING, p.y, p.vx, p.vy);
          p.vx = Math.abs(p.vx) * BOUNCE;
        }
        // Right wall
        if (p.x + halfW > w - WALL_PADDING) {
          p.x = w - WALL_PADDING - halfW;
          if (speed > 1.5) spawnBurst(w - WALL_PADDING, p.y, p.vx, p.vy);
          p.vx = -Math.abs(p.vx) * BOUNCE;
        }
        // Top wall
        if (p.y - halfH < WALL_PADDING) {
          p.y = WALL_PADDING + halfH;
          if (speed > 1.5) spawnBurst(p.x, WALL_PADDING, p.vx, p.vy);
          p.vy = Math.abs(p.vy) * BOUNCE;
        }
        // Bottom wall
        if (p.y + halfH > h - WALL_PADDING) {
          p.y = h - WALL_PADDING - halfH;
          if (speed > 1.5) spawnBurst(p.x, h - WALL_PADDING, p.vx, p.vy);
          p.vy = -Math.abs(p.vy) * BOUNCE;
        }
      }

      // Update burst particles
      for (let i = bursts.length - 1; i >= 0; i--) {
        const b = bursts[i];
        b.life++;
        b.x += b.vx;
        b.y += b.vy;
        b.vx *= 0.95;
        b.vy *= 0.95;
        b.vy += 0.1; // slight gravity on bursts
        b.rotation += b.rotSpeed;
        b.opacity = (1 - b.life / b.maxLife) * 0.8;

        if (b.life >= b.maxLife) {
          bursts.splice(i, 1);
        }
      }

      // --- RENDER ---

      // Render burst particles (behind main text)
      for (const b of bursts) {
        if (b.opacity <= 0) continue;
        ctx.save();
        ctx.translate(b.x, b.y);
        ctx.rotate(b.rotation);
        ctx.font = `${b.fontSize}px "Press Start 2P", monospace`;
        ctx.fillStyle = `rgba(0, 255, 153, ${b.opacity})`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(b.char, 0, 0);
        ctx.restore();
      }

      // Render main characters
      for (const p of particles) {
        const displacement = Math.sqrt(
          (p.x - p.originX) ** 2 + (p.y - p.originY) ** 2
        );
        const maxDisp = Math.max(w, h) * 0.5;
        const dispRatio = Math.min(displacement / maxDisp, 1);

        // Brighter when displaced, dimmer when at rest
        const alpha = 0.4 + dispRatio * 0.6;
        // Slight hue shift: green → cyan when flying
        const green = Math.round(255 - dispRatio * 40);
        const blue = Math.round(153 + dispRatio * 80);

        ctx.font = `bold ${p.fontSize}px "Press Start 2P", monospace`;
        ctx.fillStyle = `rgba(0, ${green}, ${blue}, ${alpha})`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        // Rotation based on velocity
        const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
        const rot = speed > 0.5 ? Math.atan2(p.vy, p.vx) * dispRatio * 0.2 : 0;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(rot);
        ctx.fillText(p.char, 0, 0);
        ctx.restore();
      }

      // Draw subtle tilt indicator in center
      if (tiltMag > 0.05) {
        const cx = w / 2;
        const cy = h / 2;
        const indicatorLen = 30;
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx + tx * indicatorLen, cy + ty * indicatorLen);
        ctx.strokeStyle = `rgba(0, 255, 153, ${Math.min(tiltMag * 0.3, 0.15)})`;
        ctx.lineWidth = 2;
        ctx.stroke();

        // Circle at center
        ctx.beginPath();
        ctx.arc(cx, cy, 4, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 255, 153, ${isLevel ? 0.3 : 0.08})`;
        ctx.fill();
      }
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseleave', onMouseLeave);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('resize', resize);
    window.addEventListener('deviceorientation', onOrientation);

    document.fonts.ready.then(() => {
      resize();
      animRef.current = requestAnimationFrame(animate);
    });

    // Detect device capability
    const DOE = DeviceOrientationEvent as any;
    if (typeof DOE.requestPermission === 'function') {
      // iOS 13+ — needs user gesture to request permission
      setPermissionState('prompt');
    } else if ('DeviceOrientationEvent' in window) {
      // Android or older iOS — try listening, check if we get real data
      setPermissionState('granted');
      let gotData = false;
      const testHandler = (e: DeviceOrientationEvent) => {
        if (e.gamma !== null || e.beta !== null) {
          gotData = true;
        }
      };
      window.addEventListener('deviceorientation', testHandler);
      setTimeout(() => {
        window.removeEventListener('deviceorientation', testHandler);
        if (!gotData) {
          // No gyroscope data — desktop or no sensor
          setPermissionState('desktop');
        }
      }, 1000);
    } else {
      setPermissionState('desktop');
    }

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseleave', onMouseLeave);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('resize', resize);
      window.removeEventListener('deviceorientation', onOrientation);
    };
  }, [spawnBurst]);

  // iOS 13+ requires permission request via user gesture
  const requestPermission = async () => {
    const DOE = DeviceOrientationEvent as any;
    if (typeof DOE.requestPermission === 'function') {
      try {
        const result = await DOE.requestPermission();
        if (result === 'granted') {
          setPermissionState('granted');
        }
      } catch {
        setPermissionState('desktop');
      }
    } else {
      setPermissionState('granted');
    }
  };

  return (
    <div className="textRepel">
      <canvas ref={canvasRef} className="textRepelCanvas" />

      <ExperimentNav prompt={exp?.prompt || PROMPT} title="Text Repel" />

      {permissionState === 'prompt' && (
        <div className="permissionOverlay" onClick={requestPermission}>
          <div className="permissionBox">
            <div className="permissionIcon">⟳</div>
            <div className="permissionTitle">Enable Gyroscope</div>
            <div className="permissionDesc">Tap anywhere to allow<br />device orientation access</div>
          </div>
        </div>
      )}

      <div ref={hintRef} className="repelHint">
        {permissionState === 'desktop'
          ? 'move cursor to shift gravity'
          : permissionState === 'granted'
            ? 'tilt to pour'
            : ''}
      </div>

      <div className="repelHud">
        <span>tilt: <span className="hudValue">{tiltDisplay.x}°, {tiltDisplay.y}°</span></span>
        <span>chars: <span className="hudValue">{charCount}</span></span>
        <span>bursts: <span className="hudValue">{burstCount}</span></span>
        <span>engine: <span className="hudValue">pretext</span></span>
      </div>
    </div>
  );
};

export default TextRepel;
